/**
 * Step-up controller (CAP-AC-037)
 *
 * A tiny module-level store + promise queue between the HTTP layer and the global
 * "Confirm it's you" modal. The interceptor calls `requestStepUp(submit)` when a
 * request is answered `403 STEP_UP_REQUIRED` and awaits the boolean outcome; the modal
 * subscribes to the state and calls `submitCredential` / `cancelStepUp`.
 *
 * Concurrent requests share ONE modal and ONE step-up: while a step-up is pending every
 * caller receives the same promise.
 */

export type StepUpCredential = { password: string } | { code: string }

/** Performs `POST /api/auth/step-up`; rejects with an error carrying `code` when refused. */
export type StepUpSubmit = (credential: StepUpCredential) => Promise<void>

export interface StepUpState {
  open: boolean
  busy: boolean
  /** Backend code of the last refusal (INVALID_PASSWORD, INVALID_MFA_CODE, ACCOUNT_LOCKED...). */
  errorCode: string | null
  /** The refusal is final: the original request was already rejected and only "Close" remains. */
  locked: boolean
}

const CLOSED: StepUpState = { open: false, busy: false, errorCode: null, locked: false }

let state: StepUpState = CLOSED
let pending: Promise<boolean> | null = null
let settle: ((confirmed: boolean) => void) | null = null
let submitter: StepUpSubmit | null = null
const listeners = new Set<() => void>()

function setState(next: StepUpState): void {
  state = next
  listeners.forEach((listener) => listener())
}

function finish(confirmed: boolean): void {
  const resolve = settle
  pending = null
  settle = null
  submitter = null
  resolve?.(confirmed)
}

export function getStepUpState(): StepUpState {
  return state
}

export function subscribeToStepUp(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

/** Opens the modal (once) and resolves `true` after a successful step-up, `false` on cancel or lockout. */
export function requestStepUp(submit: StepUpSubmit): Promise<boolean> {
  if (pending) return pending
  submitter = submit
  pending = new Promise<boolean>((resolve) => {
    settle = resolve
  })
  setState({ open: true, busy: false, errorCode: null, locked: false })
  return pending
}

function errorCodeOf(error: unknown): string {
  const code = (error as { code?: unknown } | null)?.code
  return typeof code === 'string' ? code : 'UNKNOWN'
}

/** Sends the credential; wrong ones leave the modal open for another try. */
export async function submitCredential(credential: StepUpCredential): Promise<void> {
  const submit = submitter
  if (!submit || state.busy || state.locked) return
  setState({ ...state, busy: true, errorCode: null })
  try {
    await submit(credential)
  } catch (error) {
    const code = errorCodeOf(error)
    if (code === 'ACCOUNT_LOCKED') {
      setState({ open: true, busy: false, errorCode: code, locked: true })
      finish(false)
    } else {
      setState({ ...state, busy: false, errorCode: code })
    }
    return
  }
  setState(CLOSED)
  finish(true)
}

/** The person dismissed the modal: the original request is rejected with its original error. */
export function cancelStepUp(): void {
  if (state.busy) return
  setState(CLOSED)
  finish(false)
}
